import { useContext, useEffect, useMemo, useState } from "react";

import find from "lodash/find";
import { useRouter } from "next/router";
import useSWR from "swr";

import { BaseContext } from "~context";

import { sdk } from "~lib";

import {
  getTotals,
  Method,
  OrderGateway,
} from "~features/checkout/helpers/getTotals";
import { usePointsService } from "~features/points/hooks/usePointsService";
import {
  GetPosQuery,
  GetReferralCampaignByOrderIdQuery,
  OrderQuery,
  OrderStatus,
  OrderTransactionStatus,
  OrderType,
  UpdateOrderInput,
  VenueSeating,
} from "~graphql/sdk";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import {
  ExtendedGraphQLError,
  getGraphQLError,
  handlePromise,
  showToast,
} from "~lib/helpers";
import { usePayment } from "./usePayment";
import { useSteps } from "./useSteps";
import { useMultibuy } from "~hooks/multibuy/non-seated";
import { useSeatedMultibuy } from "~hooks/multibuy/seated";
import { useAccount } from "~hooks/useAccount";
import { useReferral } from "~hooks/useReferral";
import { useTicketCover } from "~hooks/checkout/useTicketCover";

const getOrder = async (_: string, orgId: string, orderId: string) =>
  sdk({ orgId })
    .order({ id: orderId })
    .then(({ order }) => {
      if (order?.id) {
        return order;
      }

      throw Error("Order not found");
    });

const getReferalCampaign = async (_: string, orgId: string, id: string) => {
  return sdk({ orgId }).getReferralCampaignByOrderId({
    id,
  });
};

const getPOS = async (_: string, orgId: string, posId: string) =>
  sdk({ orgId })
    .getPOS({ id: posId })
    .then((res) => res.pos);

const swrKey = "order";

interface useCheckoutProps {
  unsetUnsavedChangesWarning?: () => void;
  onUpdateOrderError?: (error: ExtendedGraphQLError) => void;
}

export const useCheckout = ({
  unsetUnsavedChangesWarning,
  onUpdateOrderError,
}: useCheckoutProps) => {
  const router = useRouter();
  const { isAdmin, user, isEventManager, isSalesOutlet } = useAccount();
  const { organization } = useContext(BaseContext);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [transactionError, setTransactionError] = useState(null);
  const [gateways, setGateways] = useState<OrderGateway[]>([]);
  const [gateway, setGateway] = useState<OrderGateway>(undefined);
  const [method, setMethod] = useState<Method>(gateways?.[0]?.id);
  const [partialPaymentMethod, setPartialPaymentMethod] = useState<Method>(
    null
  );
  const { calculateReferralDiscount } = useReferral();
  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const { data: order, error, mutate } = useSWR<OrderQuery["order"], Error>(
    organization?.id && router?.query?.orderId
      ? [swrKey, organization?.id, router?.query?.orderId]
      : null,
    getOrder,
    {
      shouldRetryOnError: true,
      // when the payment is initiated refresh every 5 seconds
      ...(shouldPoll && { refreshInterval: 5000 }),
    }
  );
  const { calculateTicketCoverPrice, ticketCoverAmount } = useTicketCover(
    recaptchaSdkGenerator
  );

  const isBackOfficeOrder = isAdmin || isEventManager || isSalesOutlet;

  const { point } = usePointsService({
    enabled: order?.orderType === OrderType.PointPurchase,
  });

  const isPointsOrder = order?.orderType === OrderType.PointPurchase && point;

  const { data: posData, error: posError } = useSWR<GetPosQuery["pos"], Error>(
    organization?.id && order?.pos?.id
      ? ["pos", organization?.id, order?.pos?.id]
      : null,
    getPOS
  );

  const lineItems = useMemo(
    () => order?.lineItems?.edges?.map(({ node }) => node),
    [order?.id]
  );

  const { activePromotions: seatedPromotions } = useSeatedMultibuy(
    order?.event?.multiBuyPromotions || order?.membership?.multiBuyPromotions,
    lineItems
  );

  const { activePromotions: promotions } = useMultibuy(
    order?.event?.multiBuyPromotions || order?.membership?.multiBuyPromotions,
    lineItems
  );

  const { step, navigateSteps } = useSteps({
    deliveryMethod: order?.deliveryMethod,
    buyerInformation: order?.buyerInformation,
    status: order?.status,
    transactionStatus: order?.transactionStatus,
    isAdmin,
  });

  const { data: referralCampaign } = useSWR<GetReferralCampaignByOrderIdQuery>(
    router?.query?.orderId
      ? ["referralCampaign", organization?.id, router?.query?.orderId]
      : null,
    getReferalCampaign
  );

  const ticketTotal = order?.lineItems?.edges?.reduce(
    (sum, lineItem) => sum + lineItem?.node?.price * lineItem?.node?.quantity,
    0
  );

  const referralReduction = calculateReferralDiscount(
    referralCampaign?.getReferralCampaignByOrderId,
    ticketTotal
  );

  const { totalWithTransactionFee, ...totals } = getTotals({
    step,
    order,
    method,
    gateway,
    gateways,
    userPoints: isBackOfficeOrder ? order?.user?.points : user?.points,
    userCredits: isBackOfficeOrder ? order?.user?.credits : user?.credits,
    referralReduction,
    partialPaymentMethod,
    ticketCoverAmount,
  });

  // recalculate ticket cover price whenever gateway changes
  useEffect(() => {
    if (order?.event?.ticketCover?.isEnabled) {
      const totalWithoutTicketCover =
        totalWithTransactionFee - totals.ticketCoverTotal;

      void (async () => {
        await handlePromise(
          async () =>
            await calculateTicketCoverPrice(order, totalWithoutTicketCover)
        );
      })();
    }
  }, [gateway]);

  // preselect first gateway if ticket cover and gateways are enabled
  // ensures ticket cover amount shows including transaction fees
  useEffect(() => {
    if (
      order?.event?.ticketCover?.isEnabled &&
      gateways?.length > 0 &&
      !method
    ) {
      setMethod(gateways[0].id);
    }
  }, [order, gateways]);

  const { createPayment, applyCoupon, removeCoupon } = usePayment({
    orderId: order?.id,
    gateways,
    unsetUnsavedChangesWarning,
  });

  useEffect(() => {
    if (order?.status && order?.transactionStatus) {
      if (
        [OrderTransactionStatus.Error, OrderTransactionStatus.Failure].includes(
          order?.transactionStatus
        )
      ) {
        setTransactionError(true);
        setShouldPoll(false);
      }

      if (
        order?.status === OrderStatus.Pending &&
        order?.transactionStatus === OrderTransactionStatus.Pending
      ) {
        if (!shouldPoll) {
          setShouldPoll(true);
        }
      } else if (shouldPoll) {
        setShouldPoll(false);
      }
    }
  }, [order?.status, order?.transactionStatus]);

  useEffect(() => {
    let gateways: OrderGateway[] = [];

    if (order?.membership) {
      gateways = [...gateways, ...(order.membership?.gateways || [])];
      setGateways(gateways);
    }

    if (order?.event) {
      gateways = [...gateways, ...(order.event?.gateways || [])];
    }
    setGateways(gateways);

    if (isPointsOrder) {
      gateways = [...gateways, ...point.gateways];
    }

    setGateways(gateways);
    setGateway(find(gateways, { id: method }));
  }, [
    order?.membership,
    order?.event,
    isPointsOrder,
    method,
    user,
    order?.user,
  ]);

  const updateOrder = async (update: UpdateOrderInput) => {
    const sdkFn = await recaptchaSdkGenerator("updateOrder");
    const { error } = await handlePromise(async () =>
      sdkFn({ orgId: organization?.id }).updateOrder({
        id: order?.id,
        input: { ...update, step },
      })
    );

    if (error) {
      const e = getGraphQLError(error);
      if (onUpdateOrderError) {
        return onUpdateOrderError(e);
      }

      return showToast(e.message, "error");
    }

    void mutate(undefined, true);
  };

  return {
    point,
    points: isPointsOrder && lineItems[0],
    isPointsOrder,
    gateways,
    activePromotions:
      (order?.event
        ? order?.event?.venue?.seating
        : order?.membership
        ? order?.membership?.venue?.seating
        : undefined) === VenueSeating.NonSeated
        ? promotions
        : seatedPromotions,
    applyCoupon,
    createPayment,
    error,
    isLoading: !order && !error,
    isLoadingPOS: !posData && !posError,
    isProcessing: shouldPoll,
    isNonSeated: isPointsOrder
      ? true
      : (order?.event
          ? order?.event?.venue?.seating
          : order?.membership
          ? order?.membership?.venue?.seating
          : undefined) === VenueSeating.NonSeated,
    method,
    mutate,
    navigateSteps,
    order,
    posData,
    removeCoupon,
    setMethod,
    partialPaymentMethod,
    setPartialPaymentMethod,
    setProcessing: setShouldPoll,
    step,
    totals: {
      ...totals,
      totalWithTransactionFee,
    },
    updateOrder,
    transactionError,
    setTransactionError,
    referralCampaign,
    ticketCoverAmount,
  };
};
